import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { ProcessSteps } from '@/components/ProcessStep'
import { useLuxeHero, useLuxeReveal, useLuxeGrid } from '@gjl/motion/css'
import { ArrowRight, CheckCircle, Users, Briefcase } from 'lucide-react'

export default function InvestPage() {
  const heroRef = useLuxeHero<HTMLDivElement>(0.2)
  const introRef = useLuxeReveal<HTMLDivElement>('.reveal')
  const rolesRef = useLuxeGrid<HTMLDivElement>('.role-card')
  const processRef = useLuxeReveal<HTMLDivElement>('.reveal')
  const ctaRef = useLuxeReveal<HTMLDivElement>('.reveal')

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="bg-gradient-dark-gold absolute inset-0" />
        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <p className="text-primary mb-6 text-lg font-medium tracking-wide">Partnership opportunity</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">Your Path to Passive Real Estate Income</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Build wealth through real assets, without becoming a landlord.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section ref={introRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">
          <div className="reveal space-y-6">
            <h2 className="text-fluid-h2 text-foreground">Real Estate Without the Landlord Work</h2>
            <p className="text-fluid-body-lg text-muted-foreground">
              You're earning well. You've built up savings. But your wealth is stuck in volatile markets.
            </p>
            <p className="text-foreground text-xl font-semibold">
              You want the benefits of real estate ownership without the hassles of being a landlord.
            </p>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Imagine This Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent>
            <h2 className="text-fluid-h2 text-foreground mb-6">Imagine This Instead</h2>
            <p className="text-fluid-body-lg text-muted-foreground mb-6">
              You receive quarterly reports showing real properties generating real income. The numbers aren't exciting
              - they're just steady, predictable, and growing.
            </p>
            <p className="text-primary text-xl font-semibold">
              This is what partnering with Fairmeadow Capital looks like.
            </p>
          </GlassCard>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* How Partnership Works */}
      <section ref={rolesRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">How the Partnership Works</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              You bring the capital. We bring the expertise, time, and operations.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {/* Your Role */}
            <GlassCard className="role-card" hover>
              <div className="mb-6 flex items-center gap-3">
                <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-xl">
                  <Users className="text-primary h-6 w-6" />
                </div>
                <h3 className="text-foreground text-xl font-semibold">Your Role</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-muted-foreground">Provide capital ($25K-$100K per deal)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-muted-foreground">Review quarterly reports</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-muted-foreground">Receive monthly or quarterly distributions</span>
                </li>
              </ul>
            </GlassCard>

            {/* Our Role */}
            <GlassCard className="role-card" hover>
              <div className="mb-6 flex items-center gap-3">
                <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-xl">
                  <Briefcase className="text-primary h-6 w-6" />
                </div>
                <h3 className="text-foreground text-xl font-semibold">Our Role</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-muted-foreground">Source and underwrite all deals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-muted-foreground">Manage renovations and operations</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-muted-foreground">Deliver quarterly reports and distributions</span>
                </li>
              </ul>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* The Process */}
      <section ref={processRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="reveal mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">The Process</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              From discovery to distributions in three simple steps
            </p>
          </div>

          <div className="reveal">
            <ProcessSteps
              steps={[
                {
                  title: 'We Find the Deals',
                  description:
                    'We source, analyze, and underwrite small multifamily properties in Cleveland. Every deal goes through rigorous stress-testing before we present it.',
                },
                {
                  title: 'You Review & Decide',
                  description:
                    'You receive full deal packages with conservative projections, risk assessments, and our honest take. No pressure - take the time you need.',
                },
                {
                  title: 'We Handle Everything',
                  description:
                    'Acquisition, renovation, leasing, management, reporting - all handled. You receive quarterly updates and distributions.',
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Typical Deal Structure */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">Typical Deal Structure</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">Clear terms, aligned incentives</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <GlassCard className="gold-accent-left pl-8">
              <h3 className="text-foreground mb-4 text-lg font-semibold">Investment Range</h3>
              <p className="text-primary mb-2 text-3xl font-bold">$25K - $100K</p>
              <p className="text-muted-foreground">Per deal, based on property size and your comfort level</p>
            </GlassCard>

            <GlassCard className="gold-accent-left pl-8">
              <h3 className="text-foreground mb-4 text-lg font-semibold">Hold Period</h3>
              <p className="text-primary mb-2 text-3xl font-bold">5-10+ Years</p>
              <p className="text-muted-foreground">Long-term holds to maximize returns through appreciation</p>
            </GlassCard>

            <GlassCard className="gold-accent-left pl-8">
              <h3 className="text-foreground mb-4 text-lg font-semibold">Target Returns</h3>
              <p className="text-primary mb-2 text-3xl font-bold">8-12%</p>
              <p className="text-muted-foreground">Cash-on-cash returns, conservatively underwritten</p>
            </GlassCard>

            <GlassCard className="gold-accent-left pl-8">
              <h3 className="text-foreground mb-4 text-lg font-semibold">Distributions</h3>
              <p className="text-primary mb-2 text-3xl font-bold">Quarterly</p>
              <p className="text-muted-foreground">Regular distributions once properties stabilize</p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent className="reveal text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">Ready to Start Your Journey?</h2>
            <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
              Join our investor list to receive early access to upcoming deals.
            </p>
            <Link to="/contact">
              <Button size="xl">
                Join the Investor List
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
